// Copyright (c) StrangeBee 2023

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { THForm } from '@Molecules/THForm/THForm';
import { CaseResultCard } from '@Organisms/Search/SearchResultCard/CaseResultCard/CaseResultCard';

import { Form, FormInstance } from 'antd';
import { ReactElement, useMemo } from 'react';
import { CaseOutput, FilterInput } from 'thehive-sdk';

import { useCaseMerge } from './useCaseMerge';

import './CaseMerge.scss';

type SearchType = 'title' | 'number';

export interface CaseMergeFormValues {
    caseData?: CaseOutput;
    searchType?: SearchType;
    searchField?: string;
}

interface CaseMergeProps {
    caseId?: string;
    form: FormInstance;
    onChange: () => void;
    disabled: boolean;
    listCasesForMerge: (filter: FilterInput) => Promise<CaseOutput[]>;
    onFinish: (values: CaseMergeFormValues) => Promise<void>;
    onCancel: () => void;
    disallowMerge?: boolean;
}

const CaseMerge = ({
    caseId,
    form,
    onChange,
    disabled,
    listCasesForMerge,
    onFinish,
    onCancel,
    disallowMerge,
}: CaseMergeProps): ReactElement => {
    const i = useLocalMessage();
    const { caseList, handleChange, handleSubmit, stateLoading, checkSearchField } = useCaseMerge({
        form,
        listCasesForMerge,
        onFinish,
        onChange,
        disallowMerge,
    });

    const caseData = Form.useWatch<CaseOutput | undefined>(['caseData'], form);
    const searchType = Form.useWatch<SearchType | undefined>(['searchType'], form);

    const searchTypeOptions: { label: string; value: SearchType }[] = useMemo(
        () => [
            { label: i('case.merge.form.radio.title'), value: 'title' },
            { label: i('case.merge.form.radio.number'), value: 'number' },
        ],
        [i],
    );

    return (
        <THForm<CaseMergeFormValues>
            form={form}
            onFinish={handleSubmit}
            initialValues={{ searchType: 'title' }}
            onValuesChange={handleChange}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onCancel={onCancel}
                        disabled={disabled}
                        footerText={i('case.merge.submit')}
                    />
                }
            >
                <div className="case-merge__form">
                    <THForm.Item name="searchType" className="case-merge__radio-field">
                        <THRadio.Group>
                            {searchTypeOptions.map(({ label, value }) => (
                                <THRadio key={value} value={value}>
                                    {label}
                                </THRadio>
                            ))}
                        </THRadio.Group>
                    </THForm.Item>

                    <THForm.Item
                        name="searchField"
                        className="case-merge__autocomplete-field"
                        help={searchType !== 'number' ? i('case.merge.form.help') : null}
                        rules={[
                            ({ getFieldValue }) => ({
                                async validator(_, value) {
                                    const checkResult = await checkSearchField(getFieldValue('searchType'), value);
                                    return checkResult !== null ? Promise.reject(i(checkResult)) : Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <THAutoComplete<any>
                            placeholder={i(`case.merge.form.placeholder.${searchType || 'title'}`)}
                            options={caseList
                                .filter(({ _id }) => _id !== caseId)
                                .map(({ number, title }) => ({
                                    key: `#${number} - ${title}`,
                                    label: `#${number} - ${title}`,
                                    value: `#${number} - ${title}`,
                                }))}
                            onSelect={(_: any, option: any): void => {
                                const { value = '' } = option || {};
                                const parsedValue = parseInt(value.split('-').find(Boolean)?.substring(1)) || -1;
                                form.setFieldsValue({
                                    caseData: caseList.find(({ number }: CaseOutput) => number === parsedValue),
                                });
                                onChange();
                            }}
                            filterOption={false}
                            onChange={() => {
                                form.resetFields(['caseData']);
                            }}
                        />
                    </THForm.Item>
                </div>

                {caseData && <CaseResultCard data={caseData} />}

                <THForm.Item
                    name="caseData"
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <THInput hidden />
                </THForm.Item>

                <THForm.Item name="searchItem">
                    <THInput hidden />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};

export default CaseMerge;
